import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';

/** The same visible, living enemy and acting selection required by click-to-attack. */
export function isEnemyClickTarget(id: string, snapshot: WorldSnapshot | null, selected: readonly string[]): boolean {
  return !!snapshot?.heroes.some(hero => selected.includes(hero.id) && hero.connected && !hero.health.dead) &&
    !!snapshot.enemies.some(enemy => enemy.id === id && !enemy.health.dead && !enemy.hidden && !enemy.escaped);
}

/** Input mode/ownership decides whether this living connected ally may be picked. */
export function isAllyClickTarget(id: string, snapshot: WorldSnapshot | null): boolean {
  return !!snapshot?.heroes.some(hero => hero.id === id && hero.connected && !hero.health.dead);
}

export function createUnitHitArea(radius: number, height: number) {
  // Material visibility prevents a draw call; raycasting still uses the geometry.
  const hitArea = new THREE.Mesh(new THREE.CylinderGeometry(radius + .12, radius + .12, height + .05, 16), new THREE.MeshBasicMaterial({ visible: false }));
  hitArea.position.y = (height + .05) / 2;
  return hitArea;
}

export interface UnitPickTarget { id: string; group: THREE.Group; hitArea: THREE.Mesh }

const intersectionsByRaycaster = new WeakMap<THREE.Raycaster, THREE.Intersection[]>();

const axisPoint = new THREE.Vector3(), across = new THREE.Vector3(), UP = new THREE.Vector3(0, 1, 0);
/**
 * Pick only presentation proxies, never the animated rig or its equipment. When proxies overlap on screen (one figure standing in
 * front of another), the one whose upright axis the ray passes closest to wins, as a fraction of its radius; only a near tie falls
 * back to the nearest. So pointing at the middle of a figure half hidden behind another picks it, not the one whose edge is in front.
 */
export function pickUnit(raycaster: THREE.Raycaster, targets: Iterable<UnitPickTarget>): string | undefined {
  let intersections = intersectionsByRaycaster.get(raycaster);
  if (!intersections) { intersections = []; intersectionsByRaycaster.set(raycaster, intersections); }
  let best = Infinity, nearest = Infinity, picked: string | undefined;
  const { origin, direction } = raycaster.ray;
  across.crossVectors(direction, UP);
  const flat = across.lengthSq() < 1e-8;
  if (!flat) across.normalize();
  for (const { id, group, hitArea } of targets) {
    if (!group.visible || !hitArea.visible) continue; // Raycaster itself does not filter invisible objects.
    // Refresh just the proxy and its ancestors, without updating the skeleton tree.
    hitArea.updateWorldMatrix(true, false);
    intersections.length = 0;
    const hit = raycaster.intersectObject(hitArea, false, intersections)[0];
    if (!hit) continue;
    axisPoint.setFromMatrixPosition(hitArea.matrixWorld).sub(origin);
    const radius = (hitArea.geometry as THREE.CylinderGeometry).parameters?.radiusTop ?? 1;
    const offAxis = (flat ? Math.hypot(axisPoint.x, axisPoint.z) : Math.abs(axisPoint.dot(across))) / radius;
    if (offAxis < best - .15 || offAxis < best + .15 && hit.distance < nearest) { best = Math.min(best, offAxis); nearest = hit.distance; picked = id; }
  }
  return picked;
}
